import './index.styles.css'

import { DeleteOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MomentDatePicker, Texto, Vertical } from '@gravitate-js/excalibrr'
import { datePickerValidators } from '@utils/datePickerValidator'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Form, Input, InputNumber, Popconfirm, Select } from 'antd'
import moment, { type Moment } from 'moment'
import type React from 'react'
import { useMemo, useState } from 'react'

import { useEasyOrderMovements } from '../MovementStoreProvider'

export const MovementForm: React.FC<IProps> = () => {
  const [selectedDate, setSelectedDate] = useState<Date | Moment | null>(moment())

  const {
    form,
    metadata: { products },
    isOrderReadOnly,
  } = useEasyOrderMovements()

  const productOptions = useMemo(() => products.map((name) => ({ label: name, value: name })), [products])
  const requiredRule = (fieldName: string) => ({ required: true, message: `${fieldName} is required.` })

  const fieldDefs = useMemo(
    () => [
      {
        name: 'number',
        label: 'BOL #',
        type: 'text',
        editable: true,
        rules: [
          {
            validator: (_: any, value: string) => {
              const currentMovements = form.getFieldValue('movements')
              const matchingBolMovements = currentMovements.filter((m: any) => m.number === value)
              const uniqueTerminals = new Set(matchingBolMovements.map((m: any) => m.terminal))
              if (matchingBolMovements.length > 1 && uniqueTerminals.size > 1 && value !== '') {
                return Promise.reject(`BOL # ${value} is already used for a different terminal.`)
              }
              return Promise.resolve()
            },
          },
        ],
      },
      {
        name: 'date',
        label: 'BOL Date / Time',
        type: 'datetime',
        editable: true,
        rules: [
          {
            validator: (_: any, value: string) => {
              const deliveryDate = form.getFieldValue('delivery_date')
              if (moment(deliveryDate).isBefore(moment(value))) {
                return Promise.reject('BOL Date and Time must be before Delivery Date and Time')
              }
              return Promise.resolve()
            },
          },
        ],
      },
      {
        name: 'bol_product',
        label: 'BOL Product',
        type: 'select',
        options: productOptions,
        editable: true,
      },
      {
        name: 'gross_quantity',
        label: 'Gross Quantity',
        type: 'number',
        editable: true,
      },
      {
        name: 'net_quantity',
        label: 'Net Quantity',
        type: 'number',
        editable: true,
      },
      {
        name: 'drop_product',
        label: 'Tank Product',
        type: 'select',
        options: productOptions,
        editable: true,
      },
      {
        name: 'supplier',
        label: 'Supplier',
        editable: false,
      },
      {
        name: 'terminal',
        label: 'Terminal',
        editable: false,
      },
      {
        name: '',
        label: '',
        type: 'action',
      },
    ],
    [products]
  )

  return (
    <div style={{ width: '100%', overflowY: 'scroll', flex: 1 }}>
      <Vertical>
        <Horizontal
          flex='0'
          style={{ overflow: 'visible' }}
          alignItems='center'
          className='px-4 py-2 bordered'
          background='bg-2'
          verticalCenter
        >
          {fieldDefs
            .filter((f) => f.type !== 'action')
            .map((field) => (
              <Vertical verticalCenter flex={1} key={field.label} className='mr-5'>
                <Texto textTransform='uppercase' weight='600' appearance='medium' key={field.label}>
                  {field.label}
                </Texto>
              </Vertical>
            ))}
          {/** Dead button that does nothing. Keeps the header columns aligned with the rows */}
          <div />
        </Horizontal>

        <Vertical background='bg-2' flex={1} style={{ overflowY: 'auto' }}>
          <Form.List name='movements'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Horizontal
                    style={{ overflow: 'visible' }}
                    justifyContent='space-between'
                    alignItems='center'
                    background='bg-1'
                    className='p-3 border-bottom'
                    key={field.name}
                  >
                    {fieldDefs
                      .filter((def) => def.type !== 'action')
                      .map((def) => {
                        return (
                          <Vertical flex={1} key={def.label} className='mr-3' justifyContent='flex-start'>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...field}
                              name={[field.name, def.name]}
                              validateTrigger='onSubmit'
                              rules={
                                def.rules && Array.isArray(def.rules)
                                  ? [...def.rules, requiredRule(def.label)]
                                  : [requiredRule(def.label)]
                              }
                            >
                              {(() => {
                                switch (def.type) {
                                  case 'select':
                                    return <Select options={def.options} showSearch disabled={isOrderReadOnly} />
                                  case 'number':
                                    return <InputNumber style={{ width: '100%' }} disabled={isOrderReadOnly} />
                                  case 'datetime':
                                    return (
                                      <MomentDatePicker
                                        showTime
                                        showSecond={false}
                                        style={{ width: '100%' }}
                                        disabled={isOrderReadOnly}
                                        disabledDate={datePickerValidators.DATE_PAST_ONLY}
                                        disabledTime={(date) => {
                                          if (moment(date).isSame(moment(), 'day')) {
                                            return {
                                              disabledHours: datePickerValidators.HOURS_PAST_ONLY,
                                              disabledMinutes: datePickerValidators.MINUTES_PAST_ONLY,
                                            }
                                          }
                                          return {
                                            disabledHours: () => [],
                                            disabledMinutes: () => [],
                                          }
                                        }}
                                        onSelect={(date) => {
                                          setSelectedDate(date)
                                        }}
                                        onChange={(date) => {
                                          setSelectedDate(date)
                                        }}
                                      />
                                    )
                                  default:
                                    return (
                                      <Input
                                        type={def.type}
                                        placeholder={def.label}
                                        disabled={!def.editable || isOrderReadOnly}
                                      />
                                    )
                                }
                              })()}
                            </Form.Item>
                          </Vertical>
                        )
                      })}
                    {!isOrderReadOnly && (
                      <Popconfirm
                        title='Are you sure you want to delete this movement?'
                        placement='topLeft'
                        disabled={form.getFieldValue('movements').length === 1}
                        onConfirm={() => remove(index)}
                      >
                        <GraviButton
                          icon={<DeleteOutlined />}
                          error
                          disabled={form.getFieldValue('movements').length === 1}
                        />
                      </Popconfirm>
                    )}
                  </Horizontal>
                ))}
              </>
            )}
          </Form.List>
        </Vertical>
      </Vertical>
    </div>
  )
}
